import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Settings, Download, RefreshCw, X } from 'lucide-react';
import { DashboardTile, DateRange } from '../../types/dashboard';
import { PlatformMapping } from '../../services/api/dashboard.service';
import { LineChart, BarChart, WorldMap, PieChart, HorizontalBarChart, MultiLineChart, DataTable, TriangleChart, OverlapChart, PyramidChart, MonthlyListenersChart, MonthlyFollowersChart, YouTubeMusicViewsChart, YouTubeMusicViewsMetric, EditorialPlaylistMixedChart, TotalPlaylistMixedChart, AudienceReachPyramidChart, WeeklyGrowthChart, MonthlyGrowthChart, PlatformGrowthComparisonChart, BrandAffinityChart, FunnelChart, GeographicDistributionBarChart, EngagementRatesChart, InstagramGenderDistributionChart, InstagramAgeDistributionChart, YouTubeGenderDistributionChart } from '../Charts';
import ChartExpandModal from './ChartExpandModal';
import html2canvas from 'html2canvas';

interface DashboardTileProps {
  tile: DashboardTile;
  onToggle: () => void;
  isCustomizeMode: boolean;
  containerWidth?: number;
  currentBreakpoint?: string;
  dateRange?: DateRange;
  onRefresh?: (tileId: string) => void;
  refreshKey?: number;
  platformMapping?: PlatformMapping | null;
  platformMappingLoading?: boolean;
  platformMappingError?: string | null;
  userPrimaryArtistId?: string;
  onRemove?: (tileId: string) => void;
}

const DashboardTileComponent: React.FC<DashboardTileProps> = ({
  tile,
  onToggle,
  isCustomizeMode,
  containerWidth,
  currentBreakpoint,
  dateRange,
  onRefresh,
  refreshKey,
  platformMapping,
  platformMappingLoading = false,
  platformMappingError = null,
  userPrimaryArtistId,
  onRemove
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [tileSize, setTileSize] = useState({ width: 0, height: 0 });
  const [showExpandModal, setShowExpandModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);

  // Track tile dimensions for responsive content
  useEffect(() => {
    if (tileRef.current) {
      const updateTileSize = () => {
        const rect = tileRef.current?.getBoundingClientRect();
        if (rect) {
          setTileSize({ width: rect.width, height: rect.height });
        }
      };

      updateTileSize();
      
      const resizeObserver = new ResizeObserver(updateTileSize);
      resizeObserver.observe(tileRef.current);
      
      return () => resizeObserver.disconnect();
    }
  }, [tile.w, tile.h, containerWidth, currentBreakpoint]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    const handleScroll = () => {
      if (showDropdown) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showDropdown]);

  const handleDropdownToggle = () => {
    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right - 160 + window.scrollX // 160px is dropdown width
      });
    }
    setShowDropdown(!showDropdown);
  };

  const handleMenuAction = (action: string) => {
    setShowDropdown(false);
    switch (action) {
      case 'expand':
        setShowExpandModal(true);
        break;
      case 'download':
        handleDownloadChart();
        break;
      case 'refresh':
        handleRefreshChart();
        break;
    }
  };

  const handleRefreshChart = () => {
    if (onRefresh) {
      onRefresh(tile.id);
    }
  };

  const handleDownloadChart = () => {
    // Find the ECharts instance in the current tile
    const chartContainer = tileRef.current?.querySelector('.echarts-for-react div');
    if (chartContainer && (window as any).echarts) {
      const chartInstance = (window as any).echarts.getInstanceByDom(chartContainer);
      if (chartInstance) {
        // Get current chart size and scale it up for download
        const chartDom = chartInstance.getDom();
        const currentWidth = chartDom.offsetWidth;
        const currentHeight = chartDom.offsetHeight;
        
        // Generate and download the image as PNG with proper scaling
        const dataURL = chartInstance.getDataURL({
          type: 'png',
          pixelRatio: 2, // High quality
          backgroundColor: '#1C1C1C', // Match tile background (ECharts uses backgroundColor)
          excludeComponents: ['toolbox'], // Exclude non-essential components
          width: Math.max(currentWidth * 2, 1200), // Scale up from current size
          height: Math.max(currentHeight * 2, 800) // Scale up from current size
        });
        
        // Create download link
        const link = document.createElement('a');
        link.download = `${tile.title.replace(/ /g, '_').toLowerCase()}_chart.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback for non-ECharts components (like tables)
        handleTableDownload();
      }
    } else {
      handleTableDownload();
    }
  };

  const handleTableDownload = () => {
    // For tables or other non-chart components, capture as screenshot
    if (tileRef.current) {
      html2canvas(tileRef.current, {
        allowTaint: true,
        useCORS: true,
        height: tileRef.current.offsetHeight * 2,
        width: tileRef.current.offsetWidth * 2
      }).then((canvas: HTMLCanvasElement) => {
        const dataURL = canvas.toDataURL('image/png', 0.9);
        const link = document.createElement('a');
        link.download = `${tile.title.replace(/ /g, '_').toLowerCase()}_chart.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch(() => {
        alert('Download feature is not available. Please try again later.');
      });
    }
  };

  // Calculate responsive content sizes based on tile dimensions
  const getResponsiveContentSize = () => {
    const isSmall = tileSize.width < 300 || tileSize.height < 200;
    const isMedium = tileSize.width < 500 || tileSize.height < 300;
    
    return {
      fontSize: isSmall ? 'text-sm' : isMedium ? 'text-base' : 'text-xl',
      padding: isSmall ? 'px-1 pb-1' : isMedium ? 'px-2 pb-2' : 'px-3 pb-3', // Remove top padding
      headerHeight: isSmall ? 30 : isMedium ? 40 : 50,
      isCompact: isSmall
    };
  };

  const getContent = () => {
    const responsive = getResponsiveContentSize();
    
    switch (tile.id) {
      case 'downloads':
        return (
          <div className="h-full w-full overflow-visible">
            <AudienceReachPyramidChart
              artistId={undefined}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'geography':
        return (
          <div className="h-full w-full overflow-hidden">
            <GeographicDistributionBarChart
              artistId={userPrimaryArtistId} // Use current user's primary artist
              className="w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'platforms':
        return (
          <div className="h-full w-full overflow-hidden">
            <TotalPlaylistMixedChart
              platformArtistId={platformMapping?.platformArtistId}
              artistId={userPrimaryArtistId}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'monthly-listeners':
        return (
          <div className="h-full w-full overflow-hidden">
            <MonthlyListenersChart
              dateRange={dateRange}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'monthly-followers':
        return (
          <div className="h-full w-full overflow-hidden">
            <MonthlyFollowersChart
              dateRange={dateRange}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'editorial-playlists':
        return (
          <div className="h-full w-full overflow-hidden">
            <EditorialPlaylistMixedChart
              platformArtistId={platformMapping?.platformArtistId}
              artistId={userPrimaryArtistId}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'weekly-growth':
        return (
          <div className="h-full w-full overflow-hidden">
            <WeeklyGrowthChart
              artistId={undefined}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'monthly-growth':
        return (
          <div className="h-full w-full overflow-hidden">
            <MonthlyGrowthChart
              artistId={undefined}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'engagement-rates':
        return (
          <div className="h-full w-full overflow-hidden">
            <EngagementRatesChart
              className="!bg-transparent !p-0 w-full h-full"
              artistId={userPrimaryArtistId}
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'instagram-gender-distribution':
        return (
          <div className="h-full w-full overflow-hidden">
            <InstagramGenderDistributionChart
              className="!bg-transparent !p-0 w-full h-full"
              artistId={userPrimaryArtistId}
              refreshKey={refreshKey}
              size="compact"
            />
          </div>
        );
      case 'instagram-age-distribution':
        return (
          <div className="h-full w-full overflow-hidden">
            <InstagramAgeDistributionChart
              className="!bg-transparent !p-0 w-full h-full"
              artistId={userPrimaryArtistId}
              refreshKey={refreshKey}
              size="compact"
            />
          </div>
        );
      case 'youtube-gender-distribution':
        return (
          <div className="h-full w-full overflow-hidden">
            <YouTubeGenderDistributionChart
              className="!bg-transparent !p-0 w-full h-full"
              artistId={userPrimaryArtistId}
              refreshKey={refreshKey}
              size="compact"
            />
          </div>
        );
      case 'platform-growth-comparison':
        return (
          <div className="h-full w-full overflow-hidden">
            <PlatformGrowthComparisonChart
              artistId={undefined}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      case 'brand-affinity':
        return (
          <div className="h-full w-full overflow-hidden">
            <BrandAffinityChart
              artistId={userPrimaryArtistId}
              platformArtistId={platformMapping?.platformArtistId}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
              platformMappingLoading={platformMappingLoading}
              platformMappingError={platformMappingError}
            />
          </div>
        );
      case 'spotify-funnel':
        return (
          <div className="h-full w-full overflow-hidden">
            <FunnelChart
              artistId={userPrimaryArtistId}
              className="!bg-transparent !p-0 w-full h-full"
              refreshKey={refreshKey}
            />
          </div>
        );
      default:
        return (
          <div className={`${responsive.padding} h-full w-full flex items-center justify-center`}>
            <div className="text-sm-nova text-gray-300">Dashboard content</div>
          </div>
        );
    }
  };

  return (
    <div 
      ref={tileRef}
      className={`
        h-full w-full rounded-lg border transition-all duration-200 flex flex-col
        ${isCustomizeMode ? 'border-blue-500 border-dashed nova-bg-white' : 'border-[#e5e7eb] nova-bg-white hover:border-gray-400'}
        ${isCustomizeMode ? 'cursor-move' : ''}
      `} 
      style={{ borderWidth: '1px' }}
    >
      <div className="flex items-center justify-between p-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm-nova font-medium text-black uppercase">{tile.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {!isCustomizeMode && (
            <button
              ref={buttonRef}
              onClick={handleDropdownToggle}
              className="p-1 text-gray-400  transition-colors border border-[#e5e7eb] rounded"
              title="Tile options"
            >
              <MoreHorizontal size={16} />
            </button>
          )}
          {isCustomizeMode && (
            <div className="text-sm-nova text-gray-400">Drag to move</div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        {getContent()}
      </div>
      
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-[#1C1C1C] border border-[#e5e7eb] rounded-lg shadow-lg py-2 w-40 z-[9999]"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <button
            onClick={() => handleMenuAction('expand')}
            className="w-full px-4 py-2 text-left text-sm-nova text-white hover:bg-gray-700 flex items-center gap-3"
          >
            <Settings size={16} />
            EXPAND
          </button>
          <button
            onClick={() => handleMenuAction('refresh')}
            className="w-full px-4 py-2 text-left text-sm-nova text-white hover:bg-gray-700 flex items-center gap-3"
          >
            <RefreshCw size={16} />
            REFRESH
          </button>
          <button
            onClick={() => handleMenuAction('download')}
            className="w-full px-4 py-2 text-left text-sm-nova text-white hover:bg-gray-700 flex items-center gap-3"
          >
            <Download size={16} />
            DOWNLOAD
          </button>
          {onRemove && (
            <>
              <div className="border-t border-gray-700 my-2"></div>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  onRemove(tile.id);
                }}
                className="w-full px-4 py-2 text-left text-sm-nova text-red-400 hover:bg-gray-700 flex items-center gap-3"
              >
                <X size={16} />
                REMOVE
              </button>
            </>
          )}
        </div>,
        document.body
      )}
      
      <ChartExpandModal
        tile={tile}
        isOpen={showExpandModal}
        onClose={() => setShowExpandModal(false)}
        dateRange={dateRange}
        artistId={userPrimaryArtistId}
        platformArtistId={platformMapping?.platformArtistId}
      />
    </div>
  );
};

export default DashboardTileComponent;
